import { Quote } from "lucide-react";
import { useSite, usePublic } from "@/context/SiteContext";
import { assetUrl } from "@/lib/api";
import { PageHero, SectionHead, DoodleBand, LightSection } from "@/components/site/Shared";
import { LeadForm } from "@/components/site/LeadForm";
import { Newsletter } from "@/components/site/Newsletter";

const VOLUNTEER = [
  { name: "name", label: "Full name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone" },
  { name: "role", label: "How you'd like to help", type: "select", options: ["Event setup", "Greeting & sign-ups", "Def Street crew", "Workshop support", "Photography / media", "Anything"] },
  { name: "availability", label: "Availability" },
  { name: "message", label: "Notes", type: "textarea" },
];
const TINT = ["bg-aqua", "bg-quartz", "bg-mustard", "bg-lav"];

const Testimonials = () => {
  const items = usePublic("testimonials") || [];
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials">
      {items.map((t, i) => (
        <figure key={t.id} className={`${TINT[i % TINT.length]} text-ink p-7`} data-testid={`testimonial-${t.id}`}>
          <Quote className="w-7 h-7" />
          <blockquote className="text-xl leading-snug mt-3">{t.quote}</blockquote>
          <figcaption className="mt-5 text-sm uppercase tracking-wide"><strong>{t.author}</strong>{t.role && <span className="font-light"> · {t.role}</span>}</figcaption>
        </figure>
      ))}
    </div>
  );
};

const Poets = () => {
  const items = usePublic("poets") || [];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-poets">
      {items.map((p) => (
        <article key={p.id} className="group" data-testid={`poet-${p.id}`}>
          <div className="relative overflow-hidden">
            <img src={assetUrl(p.image)} alt={p.name} className="w-full aspect-[4/5] object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
            <img src="/images/doodle-overlay.png" alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
          <h3 className="text-2xl font-semibold mt-4">{p.name}</h3>
          <p className="text-sm text-mas-mute mt-1">{p.bio}</p>
          {p.link && <a href={p.link} target="_blank" rel="noopener noreferrer" className="text-sm text-passion underline mt-2 inline-block">Read more</a>}
        </article>
      ))}
    </div>
  );
};

const Gallery = () => {
  const items = usePublic("gallery") || [];
  return (
    <div className="columns-2 md:columns-3 masonry" data-testid="photo-gallery">
      {items.map((g) => (
        <figure key={g.id} className="relative group overflow-hidden" data-testid={`gallery-item-${g.id}`}>
          <img src={assetUrl(g.url)} alt={g.caption} className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
          {g.caption && <figcaption className="absolute bottom-0 inset-x-0 bg-tang text-ink text-xs font-medium px-3 py-2 translate-y-full group-hover:translate-y-0 transition-transform">{g.caption}</figcaption>}
        </figure>
      ))}
    </div>
  );
};

export default function Community() {
  const { settings: s } = useSite() || {};
  if (!s) return <div className="h-[80vh]" />;
  return (
    <div data-testid="community-page">
      <PageHero testid="community-hero" eyebrow="community" title="The program is the people" intro={s.community_intro} image={s.images?.community}>
        <a href="#volunteer" className="btn-terra" data-testid="community-volunteer-link">Volunteer</a>
        <a href="#newsletter" className="btn-line" data-testid="community-newsletter-link">Get the newsletter</a>
      </PageHero>
      <section className="container-mas py-16 sm:py-20"><SectionHead eyebrow="in their words" title="Testimonials" className="mb-10" /><Testimonials /></section>
      <LightSection><div className="container-mas py-16 sm:py-20"><SectionHead light eyebrow="featured poets" title="Voices from the mic" className="mb-12" /><Poets /></div></LightSection>
      <section className="container-mas py-16 sm:py-20"><SectionHead eyebrow="photographs" title="Scenes from the scene" className="mb-10" /><Gallery /></section>
      <DoodleBand />
      <section id="volunteer" className="bg-tea text-shell scroll-mt-20">
        <div className="container-mas py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-mustard mb-4">volunteer opportunities</p>
            <h2 className="h-section">Lend a hand. Keep it free.</h2>
            <p className="mt-5 text-shell/80 font-light">{s.volunteer_intro}</p>
          </div>
          <div className="lg:col-span-7 bg-shell text-ink p-6 sm:p-8"><LeadForm type="volunteer" fields={VOLUNTEER} submitLabel="Sign me up" successText="Thanks for volunteering!" /></div>
        </div>
      </section>
      <section id="newsletter" className="container-mas py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center scroll-mt-20">
        <SectionHead eyebrow="newsletter" title="Poems, dates and news — monthly" />
        <Newsletter dark testid="community-newsletter" />
      </section>
    </div>
  );
}
